import { HTMLAttributes } from "react"
import { makeStyles } from "tss-react/mui"
import { useField } from "formik"
import {
    faInfo,
    faCalendarExclamation,
    faBadgePercent,
    faCalendarLinesPen,
    faCalendar,
    faAlarmClock,
    faMessageSmile,
    faMegaphone,
    faHandHoldingHeart,
} from "@fortawesome/pro-solid-svg-icons"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { FormikAutoComplete } from "../../../../../shared/components/forms/Formik"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { MessageTagValue } from "../messagesTypes"
import { faPlateUtensils } from "@fortawesome/pro-light-svg-icons"

const useStyles = makeStyles()((theme) => ({
    li: {
        padding: "0 !important",
    },
    optionWrap: {
        display: "flex",
        alignItems: "flex-start",
        gap: ".5rem",
        padding: ".75rem 1rem",
    },
    colorBox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: "100%",
        color: theme.palette.common.white,
        "& > svg": {
            fontSize: ".75rem",
        },
    },
    altGrey: {
        backgroundColor: theme.palette.tailwind.slate[100],
    },
    altWhite: {
        backgroundColor: theme.palette.common.white,
    },
}))

export type MessageTagOption = Readonly<{
    value: MessageTagValue
    color: string
    icon: IconProp
}>

type MessageTagIconProps = Readonly<{
    color: string
    icon: IconProp
    className?: string
}>

export function MessageTagIcon({ color, icon, className }: MessageTagIconProps) {
    return (
        <Box className={className} style={{ backgroundColor: color }}>
            <FontAwesomeIcon icon={icon} />
        </Box>
    )
}

export const notificationOptions: MessageTagOption[] = [
    {
        value: "NEWS",
        color: tailwindPalette.sky[600],
        icon: faMegaphone,
    },
    {
        value: "INFO",
        color: tailwindPalette.sky[600],
        icon: faInfo,
    },
    {
        value: "IMPORTANT",
        color: tailwindPalette.red[600],
        icon: faCalendarExclamation,
    },
    {
        value: "OFFER",
        color: tailwindPalette.yellow[600],
        icon: faBadgePercent,
    },
    {
        value: "EVENT",
        color: tailwindPalette.purple[600],
        icon: faCalendar,
    },
    {
        value: "REMINDER",
        color: tailwindPalette.purple[600],
        icon: faAlarmClock,
    },
    {
        value: "MENU_CHANGE",
        color: tailwindPalette.green[600],
        icon: faPlateUtensils,
    },
    {
        value: "FEEDBACK",
        color: tailwindPalette.green[600],
        icon: faMessageSmile,
    },
    {
        value: "HEALTH",
        color: tailwindPalette.green[600],
        icon: faHandHoldingHeart,
    },
    // {
    //     value: "SOCIAL",
    //     color: tailwindPalette.rose[600],
    //     icon: faPartyHorn,
    // },
]

type MessageTagAutoCompleteProps = Readonly<{
    name: string
    isDialog: boolean
}>

export function MessageTagAutoComplete({ name, isDialog }: MessageTagAutoCompleteProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const [field] = useField<MessageTagOption | null>(name)
    const placeholder = isDialog ? l10n.getString("create-or-edit-message-dialog-topic-placeholder") : undefined
    const selectedOption = notificationOptions.find((opt) => opt.value === field.value?.value) ?? null

    return (
        <Stack gap="0.5rem">
            {isDialog && (
                <LocalizedStrict id="create-or-edit-message-dialog-topic-label">
                    <Typography variant="body1" fontWeight="500" color="text.primary">
                        Topic:
                    </Typography>
                </LocalizedStrict>
            )}
            <LocalizedStrict id="create-or-edit-message-field-tag-page" attrs={{ label: !isDialog }}>
                <FormikAutoComplete
                    name={name}
                    label={isDialog ? "" : "Topic"}
                    options={notificationOptions}
                    getOptionLabel={(option) =>
                        option.value
                            ? l10n.getStringForEnum("create-or-edit-message-field-tag-mode", option.value, option.value)
                            : ""
                    }
                    sx={{
                        maxWidth: "50%",
                        minWidth: "30rem",
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option) => <MessageTagOptionListItem option={option} liProps={props} />}
                    renderInput={(params) => <TextField {...params} />}
                    InputProps={
                        selectedOption
                            ? {
                                  placeholder,
                                  startAdornment: (
                                      <InputAdornment position="start">
                                          <MessageTagIcon
                                              color={selectedOption.color}
                                              icon={selectedOption.icon}
                                              className={classes.colorBox}
                                          />
                                      </InputAdornment>
                                  ),
                              }
                            : { placeholder }
                    }
                />
            </LocalizedStrict>
        </Stack>
    )
}

type MessageTagOptionListItemProps = Readonly<{
    option: MessageTagOption
    liProps: HTMLAttributes<HTMLLIElement>
}>

function MessageTagOptionListItem({ option, liProps }: MessageTagOptionListItemProps) {
    const { classes, cx } = useStyles()

    const idx = Number((liProps as any)["data-option-index"] ?? 0)
    const altClass = idx % 2 === 0 ? classes.altGrey : classes.altWhite

    return (
        <Box component="li" {...liProps} className={cx(liProps.className, classes.li, altClass)}>
            <Box className={classes.optionWrap}>
                <MessageTagIcon color={option.color} icon={option.icon} className={classes.colorBox} />
                <Stack gap=".25rem">
                    <LocalizedEnum base="create-or-edit-message-field-tag-mode" enumValue={option.value}>
                        <Typography variant="body1" color="textPrimary">
                            {option.value}
                        </Typography>
                    </LocalizedEnum>
                    <LocalizedEnum base="create-or-edit-message-field-tag-description-mode" enumValue={option.value}>
                        <Typography variant="body2" color="textSecondary">
                            {option.value}
                        </Typography>
                    </LocalizedEnum>
                </Stack>
            </Box>
        </Box>
    )
}
